import React from 'react';
import Cookies from 'js-cookie';
import confetti from 'canvas-confetti';
import styles from './LiveResume.module.css';
import * as utils from '../../utils/utils';
import Sidebar from '../Sidebar/Sidebar';

const PROCESSING_STEPS = ['structurizing', 'verifying', 'generating q&a for verification'];

export default class LiveResume extends React.Component {

	constructor(props) {
		super(props);

		this.screens = {
			Composer: 1,
			Processing: 2,
			Verification: 3,
			Failure: 4,
			Success: 5,
			StructureSummary: 6,
		};

		this.processingMessageTimer = null;
		this.processingAdvanceTimer = null;
		this.questionTimer = null;
		this.answerAdvanceTimer = null;

		this.state = {
			currentScreen: this.screens.Composer,

			// liveResumeText: 'learned about knex.js migrations',
			// liveResumeText: 'as a referral coordinator, i sent 22 referrals today to different doctors from UNLV Health center in my 8 hour shift',
			liveResumeText: 'worked with seed files in knex.js with node.js backend',
			entities: [],
			selectedUserEntityID: '',
			verificationQuestions: [],
			verificationAnswersByQuestionID: {},
			selectedAnswersByQuestionID: {},
			learningIntentQuestionIDs: [],
			verificationMisses: [],
			currentLiveResumeID: 0,
			gainedStructure: null,
			isStructureLoading: false,
			structureLoadError: '',

			isLoading: false,
			isEntityLoading: true,
			errorText: '',
			verificationErrorText: '',
			infoText: "Write a concise update on what you've just accomplished: impact, outcomes, and ownership.",
			processingMessageIndex: 0,
			currentQuestionIndex: 0,
			selectedAnswerIndexes: [],
			questionTimeLeft: 60,
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}

		this.loadMyEntities();
	}

	componentWillUnmount() {
		this.clearAllVerificationTimers();
	}

	getUserIDKey = () => {
		if (utils.isDev) {
			return 'User-Id-Dev';
		}

		return 'User-Id';
	};

	answerLettersToIndexes = (rawAnswer) => {
		const normalized = String(rawAnswer || '')
			.trim()
			.toUpperCase();

		if (!normalized) {
			return [];
		}

		const letterToIndex = {
			A: 0,
			B: 1,
			C: 2,
			D: 3,
			E: 4,
		};

		// Accept formats like "A,C", "A and C", "A / C", "A. C", and even "AC".
		let tokens = normalized.match(/\b[ABCDE]\b/g) || [];

		if (!tokens.length) {
			tokens = normalized.split('').filter((char) => Object.prototype.hasOwnProperty.call(letterToIndex, char));
		}

		const indexes = tokens
			.map((item) => letterToIndex[item])
			.filter((item) => Number.isInteger(item));

		return Array.from(new Set(indexes)).sort((a, b) => a - b);
	};

	setVerificationQuestions = ({ liveResumeID, questionRows }) => {
		const normalizedQuestionRows = Array.isArray(questionRows) ? questionRows : [];

		if (normalizedQuestionRows.length === 0) {
			throw new Error('No generated verification questions were returned.');
		}

		const verificationQuestions = normalizedQuestionRows.map((row) => ({
			questionID: Number(row.userEntityLiveResumeChoiceQuestionId),
			question: row.question || '',
			options: [row.optionA, row.optionB, row.optionC, row.optionD].map((optionValue) => optionValue || ''),
		}));

		this.setState({
			verificationQuestions,
			verificationAnswersByQuestionID: {},
			selectedAnswersByQuestionID: {},
			learningIntentQuestionIDs: [],
			verificationMisses: [],
			currentLiveResumeID: liveResumeID,
		});
	};

	areAnswerSetsEqual = (leftIndexes, rightIndexes) => {
		const left = Array.isArray(leftIndexes) ? Array.from(new Set(leftIndexes)).sort((a, b) => a - b) : [];
		const right = Array.isArray(rightIndexes) ? Array.from(new Set(rightIndexes)).sort((a, b) => a - b) : [];

		if (left.length !== right.length) {
			return false;
		}

		for (let index = 0; index < left.length; index += 1) {
			if (left[index] !== right[index]) {
				return false;
			}
		}

		return true;
	};

	getQueryUserEntityID = () => {
		const params = new URLSearchParams(window.location.search);
		const value = Number(params.get('userEntityID'));

		if (Number.isInteger(value) && value > 0) {
			return value;
		}

		return null;
	};

	loadMyEntities = () => {
		this.setState({
			isEntityLoading: true,
			errorText: '',
		}, () => {
			utils.sendRequest('/entity/getMyEntities', {})
				.then((responseData) => {
					const entities = (responseData.entities || []).map((item) => ({
						userEntityId: Number(item.userEntityId),
						name: item.name || 'Untitled Entity',
					}));

					const queryEntityID = this.getQueryUserEntityID();
					const hasQueryEntity = entities.some((item) => item.userEntityId === queryEntityID);
					const selectedUserEntityID = hasQueryEntity
						? String(queryEntityID)
						: (entities[0] ? String(entities[0].userEntityId) : '');

					this.setState({
						entities,
						selectedUserEntityID,
						isEntityLoading: false,
					});
				})
				.catch((error) => {
					this.setState({
						isEntityLoading: false,
						errorText: error && error.message ? error.message : 'Could not load your entities right now.',
					});
				});
		});
	};

	onInputChange = (event) => {
		this.setState({
			liveResumeText: event.target.value,
			errorText: '',
		});
	};

	onEntityChange = (event) => {
		this.setState({
			selectedUserEntityID: event.target.value,
			errorText: '',
		});
	};

	handleTextKeyDown = (event) => {
		if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
			this.saveLiveResume();
		}
	};

	triggerSavedConfetti = () => {
		confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
	};

	clearAllVerificationTimers = () => {
		if (this.processingMessageTimer) {
			window.clearInterval(this.processingMessageTimer);
			this.processingMessageTimer = null;
		}

		if (this.processingAdvanceTimer) {
			window.clearTimeout(this.processingAdvanceTimer);
			this.processingAdvanceTimer = null;
		}

		if (this.questionTimer) {
			window.clearInterval(this.questionTimer);
			this.questionTimer = null;
		}

		if (this.answerAdvanceTimer) {
			window.clearTimeout(this.answerAdvanceTimer);
			this.answerAdvanceTimer = null;
		}
	};

	setVerificationQuestionTimer = () => {
		if (this.questionTimer) {
			window.clearInterval(this.questionTimer);
		}

		this.questionTimer = window.setInterval(() => {
			this.setState((prevState) => {
				if (prevState.currentScreen !== this.screens.Verification) {
					return null;
				}

				if (prevState.questionTimeLeft <= 1) {
					window.clearInterval(this.questionTimer);
					this.questionTimer = null;
					this.failVerification('Verification expired because the question timer reached zero.');
					return null;
				}

				return {
					questionTimeLeft: prevState.questionTimeLeft - 1,
				};
			});
		}, 1000);
	};

	startProcessingFlow = () => {
		this.clearAllVerificationTimers();

		this.setState({
			currentScreen: this.screens.Processing,
			errorText: '',
			verificationErrorText: '',
			gainedStructure: null,
			isStructureLoading: false,
			structureLoadError: '',
			infoText: 'Saving your live resume update...',
			processingMessageIndex: 0,
		}, () => {
			this.processingMessageTimer = window.setInterval(() => {
				this.setState((prevState) => ({
					processingMessageIndex: (prevState.processingMessageIndex + 1) % PROCESSING_STEPS.length,
				}));
			}, 1050);
		});
	};

	startVerificationFlow = () => {
		this.clearAllVerificationTimers();

		if (!this.state.verificationQuestions.length) {
			this.failVerification('Verification questions are not ready yet. Please try again.');
			return;
		}

		this.setState({
			currentScreen: this.screens.Verification,
			errorText: '',
			verificationErrorText: '',
			infoText: 'Select an option for each question, then use Next to continue.',
			currentQuestionIndex: 0,
			selectedAnswerIndexes: this.state.selectedAnswersByQuestionID[this.state.verificationQuestions[0].questionID] || [],
			questionTimeLeft: 60,
		}, () => {
			this.setVerificationQuestionTimer();
		});
	};

	goToNextVerificationQuestion = () => {
		const currentQuestion = this.state.verificationQuestions[this.state.currentQuestionIndex];
		if (!currentQuestion) {
			this.failVerification('Could not load the current verification question.');
			return;
		}

		if (!Array.isArray(this.state.selectedAnswerIndexes) || this.state.selectedAnswerIndexes.length === 0) {
			this.setState({
				verificationErrorText: 'Select an answer before continuing.',
			});
			return;
		}

		const totalQuestions = this.state.verificationQuestions.length;
		const nextQuestionIndex = this.state.currentQuestionIndex + 1;
		const isFinalQuestion = nextQuestionIndex >= totalQuestions;

		this.clearAllVerificationTimers();

		if (isFinalQuestion) {
			this.verifySelectedAnswers();
			return;
		}

		const nextQuestion = this.state.verificationQuestions[nextQuestionIndex];

		this.setState({
			currentQuestionIndex: nextQuestionIndex,
			selectedAnswerIndexes: nextQuestion ? (this.state.selectedAnswersByQuestionID[nextQuestion.questionID] || []) : [],
			questionTimeLeft: 60,
			verificationErrorText: '',
		}, () => {
			this.setVerificationQuestionTimer();
		});
	};

	verifySelectedAnswers = async () => {
		const liveResumeID = Number(this.state.currentLiveResumeID);

		if (!Number.isInteger(liveResumeID) || liveResumeID <= 0) {
			this.failVerification('No live resume is loaded for answer verification. Please save again.');
			return;
		}

		this.setState({
			isLoading: true,
			verificationErrorText: '',
			infoText: 'Checking your answers...',
		});

		try {
			const answersResponse = await utils.sendRequest('/liveResume/getLiveResumeAnswers', {
				liveResumeID,
			});

			const answerRows = answersResponse && Array.isArray(answersResponse.answers)
				? answersResponse.answers
				: [];

			const verificationAnswersByQuestionID = {};
			const answerExplanationByQuestionID = {};
			answerRows.forEach((row) => {
				const questionID = Number(row.userEntityLiveResumeChoiceQuestionId);
				verificationAnswersByQuestionID[questionID] = this.answerLettersToIndexes(row.answers);
				answerExplanationByQuestionID[questionID] = row.answerExplanation || '';
			});

			const verificationMisses = [];
			this.state.verificationQuestions.forEach((question) => {
				const selectedAnswerIndexes = this.state.selectedAnswersByQuestionID[question.questionID] || [];
				const correctAnswerIndexes = verificationAnswersByQuestionID[question.questionID] || [];

				if (!this.areAnswerSetsEqual(selectedAnswerIndexes, correctAnswerIndexes)) {
					verificationMisses.push({
						questionID: question.questionID,
						question: question.question,
						options: question.options,
						selectedAnswerIndexes,
						correctAnswerIndexes,
						answerExplanation: answerExplanationByQuestionID[question.questionID] || '',
					});
				}
			});

			if (verificationMisses.length > 0) {
				this.setState({
					isLoading: false,
					currentScreen: this.screens.Failure,
					verificationAnswersByQuestionID,
					verificationMisses,
					errorText: `${verificationMisses.length} answer${verificationMisses.length > 1 ? 's were' : ' was'} missed.`,
					verificationErrorText: `${verificationMisses.length} answer${verificationMisses.length > 1 ? 's were' : ' was'} missed.`,
					infoText: 'Review what you missed, then retry verification if needed.',
					selectedAnswerIndexes: [],
					questionTimeLeft: 0,
				});
				return;
			}

			this.setState({
				isLoading: false,
				verificationAnswersByQuestionID,
				verificationMisses: [],
				currentScreen: this.screens.Success,
				verificationErrorText: '',
				infoText: 'Verification passed. Fetching your generated structure...',
				selectedAnswerIndexes: [],
				questionTimeLeft: 60,
			}, () => {
				this.triggerSavedConfetti();
				this.loadLiveResumeStructure();
			});
		} catch (error) {
			this.setState({
				isLoading: false,
				verificationErrorText: error && error.message ? error.message : 'Could not verify answers right now.',
				errorText: error && error.message ? error.message : 'Could not verify answers right now.',
				currentScreen: this.screens.Failure,
			});
		}
	};

	loadLiveResumeStructure = async () => {
		const liveResumeID = Number(this.state.currentLiveResumeID);

		if (!Number.isInteger(liveResumeID) || liveResumeID <= 0) {
			this.setState({
				currentScreen: this.screens.StructureSummary,
				isStructureLoading: false,
				gainedStructure: null,
				structureLoadError: 'No live resume was found for structure loading. Please save again.',
				infoText: 'Verification complete, but structure details are unavailable.',
			});
			return;
		}

		this.setState({
			isStructureLoading: true,
			structureLoadError: '',
			errorText: '',
		});

		try {
			const response = await utils.sendRequest('/liveResume/getLiveResumeStructure', {
				userEntityLiveResumeID: liveResumeID,
			});

			if (!response || response.status !== 'ready' || !response.structure) {
				this.setState({
					currentScreen: this.screens.StructureSummary,
					isStructureLoading: false,
					gainedStructure: null,
					structureLoadError: 'Structure is still being generated. Please retry in a few seconds.',
					infoText: 'Verification complete. Structure generation is still in progress.',
				});
				return;
			}

			this.setState({
				currentScreen: this.screens.StructureSummary,
				isStructureLoading: false,
				gainedStructure: response.structure,
				structureLoadError: '',
				infoText: 'Verification complete. Here is what was generated from your update.',
			});
		} catch (error) {
			this.setState({
				currentScreen: this.screens.StructureSummary,
				isStructureLoading: false,
				gainedStructure: null,
				structureLoadError: error && error.message ? error.message : 'Could not load generated structure right now.',
				infoText: 'Verification complete, but structure details could not be loaded.',
			});
		}
	};

	failVerification = (message) => {
		this.clearAllVerificationTimers();

		this.setState({
			currentScreen: this.screens.Failure,
			errorText: message,
			verificationErrorText: message,
			infoText: 'Verification stopped. You can retry and continue.',
			selectedAnswerIndexes: [],
			questionTimeLeft: 0,
		});
	};

	restartVerification = () => {
		if (!this.state.currentLiveResumeID || !this.state.verificationQuestions.length) {
			this.failVerification('No live resume is loaded for verification. Please save again.');
			return;
		}

		this.setState({
			errorText: '',
			verificationErrorText: '',
			verificationAnswersByQuestionID: {},
			selectedAnswersByQuestionID: {},
			learningIntentQuestionIDs: [],
			verificationMisses: [],
			infoText: 'Restarting verification...',
		}, this.startVerificationFlow);
	};

	saveLiveResume = () => {
		const liveResumeText = this.state.liveResumeText.trim();

		if (liveResumeText === '') {
			this.setState({
				errorText: 'Please enter a live resume update before saving.',
			});
			return;
		}

		const userEntityID = Number(this.state.selectedUserEntityID);
		if (!Number.isInteger(userEntityID) || userEntityID <= 0) {
			this.setState({
				errorText: 'Please select a user entity first.',
			});
			return;
		}

		this.startProcessingFlow();

		this.setState({
			isLoading: true,
		}, async () => {
			try {
				const saveResponse = await utils.sendRequest('/liveResume/saveLiveResume', {
					userEntityID,
					liveResumeText,
				});

				const liveResumeID = Number(saveResponse && saveResponse.userEntityLiveResumeID);
				if (!Number.isInteger(liveResumeID) || liveResumeID <= 0) {
					throw new Error('Save succeeded but no liveResumeID was returned.');
				}

				const questionRows = Array.isArray(saveResponse && saveResponse.questions)
					? saveResponse.questions
					: (Array.isArray(saveResponse && saveResponse.choiceQuestions) ? saveResponse.choiceQuestions : []);

				this.setVerificationQuestions({
					liveResumeID,
					questionRows,
				});

				this.setState({
					isLoading: false,
					infoText: 'Save complete. Starting verification...',
				}, this.startVerificationFlow);
			}
			catch (error) {
				this.setState({
					currentScreen: this.screens.Composer,
					isLoading: false,
					errorText: error && error.message ? error.message : 'Could not save live resume right now. Please try again.',
					infoText: "Write a concise update on what you've just accomplished: impact, outcomes, and ownership.",
				});
			}
		});
	};

	handleAnswerSelect = (answerIndex) => {
		const {
			currentScreen,
			currentQuestionIndex,
			verificationQuestions,
			selectedAnswerIndexes,
		} = this.state;

		if (currentScreen !== this.screens.Verification) {
			return;
		}

		const currentQuestion = verificationQuestions[currentQuestionIndex];
		if (!currentQuestion) {
			this.failVerification('Could not load the current verification question.');
			return;
		}

		const questionID = currentQuestion.questionID;
		const currentSelectedAnswerIndexes = Array.isArray(selectedAnswerIndexes) ? selectedAnswerIndexes : [];
		const nextSelectedAnswerIndexes = currentSelectedAnswerIndexes.includes(answerIndex)
			? currentSelectedAnswerIndexes.filter((item) => item !== answerIndex)
			: [...currentSelectedAnswerIndexes, answerIndex].sort((a, b) => a - b);

		const nextLearningIntentQuestionIDs = nextSelectedAnswerIndexes.includes(4)
			? Array.from(new Set([...this.state.learningIntentQuestionIDs, questionID]))
			: this.state.learningIntentQuestionIDs.filter((item) => item !== questionID);

		this.setState({
			selectedAnswerIndexes: nextSelectedAnswerIndexes,
			selectedAnswersByQuestionID: {
				...this.state.selectedAnswersByQuestionID,
				[questionID]: nextSelectedAnswerIndexes,
			},
			learningIntentQuestionIDs: nextLearningIntentQuestionIDs,
			verificationErrorText: '',
			infoText: nextSelectedAnswerIndexes.includes(4)
				? 'You included the learning option for this question. Click Next to continue.'
				: 'Answer selected. Click Next to continue.',
		});
	};

	goToDashboard = () => {
		window.location.href = '/dashboard';
	};

	goBackToComposer = () => {
		this.clearAllVerificationTimers();
		this.setState({
			currentScreen: this.screens.Composer,
			isLoading: false,
			errorText: '',
			verificationErrorText: '',
			verificationQuestions: [],
			verificationAnswersByQuestionID: {},
			selectedAnswersByQuestionID: {},
			learningIntentQuestionIDs: [],
			verificationMisses: [],
			currentLiveResumeID: 0,
			gainedStructure: null,
			isStructureLoading: false,
			structureLoadError: '',
			infoText: "Write a concise update on what you've just accomplished: impact, outcomes, and ownership.",
			processingMessageIndex: 0,
			currentQuestionIndex: 0,
			selectedAnswerIndexes: [],
			questionTimeLeft: 60,
		});
	};

	renderComposerScreen = () => {
		const {
			entities,
			selectedUserEntityID,
			isEntityLoading,
			liveResumeText,
			isLoading,
		} = this.state;

		return (
			<div className={styles.form}>
				<div className={styles.fieldGroup}>
					<label className={styles.label} htmlFor='user-entity'>Live Resume For</label>
					<div className={styles.selectWrap}>
						<select
							id='user-entity'
							name='userEntity'
							className={styles.selectInput}
							value={selectedUserEntityID}
							onChange={this.onEntityChange}
							disabled={isEntityLoading || isLoading}
						>
							<option value=''>
								{isEntityLoading ? 'Loading entities...' : 'Choose an entity'}
							</option>
							{entities.map((entity) => {
								return (
									<option key={`entity-${entity.userEntityId}`} value={String(entity.userEntityId)}>
										{entity.name}
									</option>
								);
							})}
						</select>
					</div>
				</div>

				<div className={styles.fieldGroup}>
					<label className={styles.label} htmlFor='live-resume-text'>Live Resume Update</label>
					<textarea
						id='live-resume-text'
						name='liveResumeText'
						className={styles.textArea}
						placeholder='Example: Led migration to typed APIs across 4 services, reduced production incidents by 35%, and mentored two engineers through rollout.'
						value={liveResumeText}
						onChange={this.onInputChange}
						onKeyDown={this.handleTextKeyDown}
					/>
				</div>

				<button className={styles.primaryButton} onClick={this.saveLiveResume} disabled={isLoading}>
					{isLoading ? 'Saving...' : 'Save Live Resume'}
				</button>

				<p className={styles.hintText}>Tip: Press Ctrl/Cmd + Enter to save quickly.</p>
			</div>
		);
	};

	renderProcessingScreen = () => {
		const {
			processingMessageIndex,
		} = this.state;

		return (
			<div className={styles.processingWrap}>
				<div className={styles.processingOrb} />
				<div className={styles.processingCopy}>
					<div className={styles.processingLabel}>Processing saved update</div>
					<h3 className={styles.processingTitle}>Preparing your verification flow</h3>
					<p className={styles.processingText}>We are turning your live resume into a focused, anti-cheat verification sequence.</p>
				</div>

				<div className={styles.processingStatusCard}>
					{PROCESSING_STEPS.map((step, index) => {
						const isActive = index === processingMessageIndex;

						return (
							<div key={step} className={`${styles.processingStep} ${isActive ? styles.processingStepActive : ''}`}>
								<span className={styles.processingDot} />
								<span>{step}</span>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	renderVerificationScreen = () => {
		const {
			currentQuestionIndex,
			selectedAnswerIndexes,
			questionTimeLeft,
			verificationQuestions,
			isLoading,
		} = this.state;

		const currentQuestion = verificationQuestions[currentQuestionIndex];
		const totalQuestions = verificationQuestions.length;

		if (!currentQuestion) {
			return (
				<div className={styles.verificationWrap}>
					<div className={styles.verificationBanner}>
						Loading verification questions...
					</div>
				</div>
			);
		}

		return (
			<div className={styles.verificationWrap}>
				<div className={styles.verificationTopRow}>
					<div className={styles.verificationPill}>Question {currentQuestionIndex + 1} of {totalQuestions}</div>
					<div className={styles.verificationPill}>{questionTimeLeft}s remaining</div>
				</div>

				<div className={styles.verificationBanner}>
					Choose the best option and press Next. Option E marks that you want to learn this topic.
				</div>

				<div className={styles.questionCard}>
					<h3 className={styles.questionText}>{currentQuestion.question}</h3>

					<div className={styles.optionsList}>
						{[...currentQuestion.options, "I don't know the answer, but I want to learn about this"].map((option, optionIndex) => {
							const isSelected = Array.isArray(selectedAnswerIndexes) && selectedAnswerIndexes.includes(optionIndex);
							const optionClassName = [
								styles.optionButton,
								isSelected ? styles.optionButtonSelected : '',
							].filter(Boolean).join(' ');

							return (
								<button
									key={`${currentQuestionIndex}-${optionIndex}`}
									className={optionClassName}
									onClick={() => this.handleAnswerSelect(optionIndex)}
									disabled={isLoading}
								>
									<span className={styles.optionLetter}>{String.fromCharCode(65 + optionIndex)}.</span>
									<span className={styles.optionText}>{option}</span>
								</button>
							);
						})}
					</div>

					<button
						className={styles.primaryButton}
						onClick={this.goToNextVerificationQuestion}
						disabled={!Array.isArray(selectedAnswerIndexes) || selectedAnswerIndexes.length === 0 || isLoading}
					>
						{currentQuestionIndex + 1 === totalQuestions ? (isLoading ? 'Checking...' : 'Finish & Check Answers') : 'Next'}
					</button>
				</div>
			</div>
		);
	};

	renderFailureScreen = () => {
		const { verificationErrorText, verificationMisses } = this.state;
		const hasMissedAnswers = Array.isArray(verificationMisses) && verificationMisses.length > 0;

		return (
			<div className={styles.failureWrap}>
				<div className={styles.failureBadge}>{hasMissedAnswers ? 'Verification result' : 'Verification stopped'}</div>
				<h3 className={styles.failureTitle}>{hasMissedAnswers ? 'Some answers were missed' : 'Verification interrupted'}</h3>
				<p className={styles.failureText}>{verificationErrorText || 'The verification session was interrupted.'}</p>

				{hasMissedAnswers && (
					<div className={styles.missedAnswersList}>
						{verificationMisses.map((item) => {
							const baseOptions = Array.isArray(item.options) ? item.options : [];
							const selectedAnswerIndexes = Array.isArray(item.selectedAnswerIndexes) ? item.selectedAnswerIndexes : [];
							const correctAnswerIndexes = Array.isArray(item.correctAnswerIndexes) ? item.correctAnswerIndexes : [];
							const shouldShowLearningOption = selectedAnswerIndexes.includes(4) || correctAnswerIndexes.includes(4);
							const renderedOptions = shouldShowLearningOption
								? [...baseOptions, "I don't know the answer, but I want to learn about this"]
								: baseOptions;

							return (
								<div key={`missed-${item.questionID}`} className={styles.missedAnswerCard}>
									<div className={styles.missedAnswerQuestion}>{item.question}</div>

									<div className={styles.reviewOptionsList}>
										{renderedOptions.map((optionText, optionIndex) => {
											const isSelected = selectedAnswerIndexes.includes(optionIndex);
											const isCorrect = correctAnswerIndexes.includes(optionIndex);
											const rowClass = [
												styles.reviewOptionRow,
												isCorrect ? styles.reviewOptionCorrect : '',
												isSelected && !isCorrect ? styles.reviewOptionWrong : '',
											].filter(Boolean).join(' ');

											return (
												<div key={`review-${item.questionID}-${optionIndex}`} className={rowClass}>
													<span className={styles.reviewOptionLetter}>{String.fromCharCode(65 + optionIndex)}.</span>
													<span className={styles.reviewOptionText}>{optionText}</span>
													{isSelected && <span className={styles.reviewSelectedTag}>Selected</span>}
													{isCorrect && <span className={styles.reviewCheckIcon}>Correct</span>}
												</div>
											);
										})}
									</div>

									{
										item.answerExplanation &&
										<div className={styles.missedAnswerMeta}>Explanation: {item.answerExplanation}</div>
									}
								</div>
							);
						})}
					</div>
				)}

				<div className={styles.failureActions}>
					<button className={styles.primaryButton} onClick={this.restartVerification}>
						Retry Verification
					</button>
					<button className={styles.secondaryButton} onClick={this.goBackToComposer}>
						Back To Composer
					</button>
				</div>
			</div>
		);
	};

	renderSuccessScreen = () => {
		return (
			<div className={styles.successWrap}>
				<div className={styles.successIcon}>Verified</div>
				<h3 className={styles.successTitle}>Live Resume Verified</h3>
				<p className={styles.successText}>Your update passed verification. Preparing your gained structure summary...</p>
			</div>
		);
	};

	renderStructureSummaryScreen = () => {
		const {
			isStructureLoading,
			gainedStructure,
			structureLoadError,
		} = this.state;

		if (isStructureLoading) {
			return (
				<div className={styles.successWrap}>
					<div className={styles.successIcon}>Preparing</div>
					<h3 className={styles.successTitle}>Building your summary</h3>
					<p className={styles.successText}>We are collecting your skills, tools, domains, soft skills, impacts, and achievements.</p>
				</div>
			);
		}

		if (!gainedStructure) {
			return (
				<div className={styles.failureWrap}>
					<div className={styles.failureBadge}>Structure pending</div>
					<h3 className={styles.failureTitle}>Not ready yet</h3>
					<p className={styles.failureText}>{structureLoadError || 'Structure generation is still in progress.'}</p>

					<div className={styles.failureActions}>
						<button className={styles.primaryButton} onClick={this.loadLiveResumeStructure}>
							Retry Loading Structure
						</button>
						<button className={styles.secondaryButton} onClick={this.goBackToComposer}>
							Back To Composer
						</button>
					</div>
				</div>
			);
		}

		const skills = Array.isArray(gainedStructure.skills) ? gainedStructure.skills : [];
		const tools = Array.isArray(gainedStructure.tools) ? gainedStructure.tools : [];
		const domains = Array.isArray(gainedStructure.domains) ? gainedStructure.domains : [];
		const softSkills = Array.isArray(gainedStructure.softSkills) ? gainedStructure.softSkills : [];
		const impacts = Array.isArray(gainedStructure.impacts) ? gainedStructure.impacts : [];
		const achievements = Array.isArray(gainedStructure.achievements) ? gainedStructure.achievements : [];

		return (
			<div className={styles.structureWrap}>
				<div className={styles.structureCard}>
					<h3 className={styles.structureTitle}>Skills</h3>
					<p className={styles.structureText}>{skills.length ? skills.map((item) => item.name).join(', ') : 'No skills captured.'}</p>
				</div>

				<div className={styles.structureCard}>
					<h3 className={styles.structureTitle}>Tools</h3>
					<p className={styles.structureText}>{tools.length ? tools.map((item) => item.name).join(', ') : 'No tools captured.'}</p>
				</div>

				<div className={styles.structureCard}>
					<h3 className={styles.structureTitle}>Domains</h3>
					<p className={styles.structureText}>{domains.length ? domains.map((item) => item.name).join(', ') : 'No domains captured.'}</p>
				</div>

				<div className={styles.structureCard}>
					<h3 className={styles.structureTitle}>Soft Skills</h3>
					<p className={styles.structureText}>{softSkills.length ? softSkills.map((item) => item.name).join(', ') : 'No soft skills captured.'}</p>
				</div>

				<div className={styles.structureCard}>
					<h3 className={styles.structureTitle}>Impacts</h3>
					<p className={styles.structureText}>{impacts.length ? impacts.map((item) => item.impact).join(' | ') : 'No impacts captured.'}</p>
				</div>

				<div className={styles.structureCard}>
					<h3 className={styles.structureTitle}>Achievements</h3>
					<p className={styles.structureText}>{achievements.length ? achievements.map((item) => item.achievement).join(' | ') : 'No achievements captured.'}</p>
				</div>

				<button className={styles.primaryButton} onClick={this.goToDashboard}>
					Back To Dashboard
				</button>
				<button className={styles.secondaryButton} onClick={this.goBackToComposer}>
					Save Another Update
				</button>
			</div>
		);
	};

	renderCurrentScreen = () => {
		const {
			currentScreen,
		} = this.state;

		if (currentScreen === this.screens.Composer) {
			return this.renderComposerScreen();
		}

		if (currentScreen === this.screens.Processing) {
			return this.renderProcessingScreen();
		}

		if (currentScreen === this.screens.Verification) {
			return this.renderVerificationScreen();
		}

		if (currentScreen === this.screens.Failure) {
			return this.renderFailureScreen();
		}

		if (currentScreen === this.screens.Success) {
			return this.renderSuccessScreen();
		}

		if (currentScreen === this.screens.StructureSummary) {
			return this.renderStructureSummaryScreen();
		}

		return null;
	};

	render() {
		const {
			errorText,
			infoText,
			currentScreen,
			currentQuestionIndex,
		} = this.state;

		let stepIndex = 0;
		if (currentScreen === this.screens.Processing || currentScreen === this.screens.Verification) {
			stepIndex = 1;
		} else if (
			currentScreen === this.screens.Failure ||
			currentScreen === this.screens.Success ||
			currentScreen === this.screens.StructureSummary
		) {
			stepIndex = 2;
		}

		return (
			<div className={styles.pageContainer}>
                <Sidebar />

				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

				<div className={styles.layoutShell}>
					<section className={styles.headerPanel}>
						{/* <div className={styles.brandBadge}>HQVibe</div> */}
						<h1 className={styles.title}>Capture your latest contribution while it is fresh.</h1>
						<p className={styles.subtitle}>
							Write a concise update on what you just accomplished.
						</p>

						<div className={styles.userRow}>
							<span className={styles.userCaption}>Focus on impact, outcomes, and ownership.</span>
						</div>
					</section>

					<section className={styles.mainCardPanel}>
						<div className={styles.stepRow}>
							<div className={`${styles.stepDot} ${stepIndex >= 0 ? styles.stepDotActive : ''}`} />
							<div className={styles.stepLine} />
							<div className={`${styles.stepDot} ${stepIndex >= 1 ? styles.stepDotActive : ''}`} />
							<div className={styles.stepLine} />
							<div className={`${styles.stepDot} ${stepIndex >= 2 ? styles.stepDotActive : ''}`} />
						</div>

						<h2 className={styles.formTitle}>
							{currentScreen === this.screens.Composer && 'Live Resume Check-In'}
							{currentScreen === this.screens.Processing && 'Preparing Verification'}
							{currentScreen === this.screens.Verification && `Verification Question ${currentQuestionIndex + 1}`}
							{currentScreen === this.screens.Failure && 'Verification Results'}
							{currentScreen === this.screens.Success && 'Verification Complete'}
							{currentScreen === this.screens.StructureSummary && 'Generated Structure Summary'}
						</h2>
						<p className={styles.formText}>{infoText}</p>

						{this.renderCurrentScreen()}

						{
							errorText !== '' && currentScreen !== this.screens.Failure &&
							<div className={styles.errorBox}>{errorText}</div>
						}
					</section>
				</div>
			</div>
		);
	}
}
